import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { gmailExpensesService } from '../services/gmailExpenses';

const GmailOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing Gmail authorization...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          setStatus('error');
          setMessage(`Authorization failed: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('Authorization code not received from Google');
          return;
        }

        // Complete OAuth flow
        await gmailExpensesService.completeOAuth(code, state || '');
        setStatus('success');
        setMessage('Gmail account connected successfully!');
        
        // Redirect to Gmail Connect page after 2 seconds
        setTimeout(() => {
          navigate('/gmail-connect');
        }, 2000);
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.message || err.message || 'Failed to connect Gmail account');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {status === 'loading' && (
            <Box>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">{message}</Typography>
            </Box>
          )}

          {status === 'success' && (
            <Box>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Success!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting to Gmail Connect page...
              </Typography>
            </Box>
          )}

          {status === 'error' && (
            <Box>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Connection Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/gmail-connect')}
              >
                Go to Gmail Connect
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default GmailOAuthCallback;
